import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { getCookie, deleteCookie } from './utils/cookies';
import { authApi, syncApi } from './api';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer/Footer';
import ScrollToTop from './components/common/ScrollToTop';
import BetaBanner from './components/common/BetaBanner';
import CookieConsent from './components/common/CookieConsent';
import Welcome from './pages/Welcome';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';
import VerifyEmail from './components/auth/VerifyEmail';
import Discover from './pages/Discover';
import Friends from './pages/Friends';
import Wishlist from './pages/Wishlist';
import MeinRegal from './pages/MeinRegal';
import About from './pages/About';
import Docs from './pages/Docs';
import Contact from './pages/Contact';
import Impressum from './pages/Impressum';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Profile from './pages/Profile';
import FriendProfile from './pages/FriendProfile';
import Settings from './pages/Settings';
import AddVolume from './pages/AddVolume';
import Admin from './pages/Admin';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { ConfirmProvider } from './context/ConfirmContext';
import './App.css';

// Info-Seiten die den WelcomeHeader verwenden (keinen App-Header)
const INFO_PAGES = ['/about', '/docs', '/contact', '/impressum', '/datenschutz', '/privacy', '/agb', '/terms'];

// Wrapper-Komponente um useLocation innerhalb Router zu nutzen
function AppContent({ loggedIn, setLoggedIn, username, setUsername, userId, setUserId, roleLevel, setRoleLevel, sessionChecked }) {
  const location = useLocation();
  
  // Prüfen ob aktuelle Seite eine Info-Seite ist
  const isInfoPage = INFO_PAGES.includes(location.pathname);
  
  // Header nur anzeigen wenn eingeloggt UND nicht auf Info-Seite UND nicht auf Welcome
  const isWelcomePage = location.pathname === '/';
  const showAppHeader = loggedIn && !isInfoPage && !isWelcomePage;

  return (
    <div className="App">
      {/* Scroll nach oben bei Routenwechsel */}
      <ScrollToTop />

      {/* App Header nur für eingeloggte User auf App-Seiten */}
      {showAppHeader && <Header loggedIn={loggedIn} setLoggedIn={setLoggedIn} username={username} userId={userId} roleLevel={roleLevel} />}

      {/* Beta-Banner: sticky direkt unter dem Header, ohne Inhalte zu überdecken */}
      {showAppHeader && (
        <div style={{
          position: 'sticky',
          top: '64px',
          zIndex: 990,
          marginTop: '64px',
        }}>
          <BetaBanner />
        </div>
      )}

      <main className={showAppHeader ? "main-content" : ""}>
        <Routes>
          {/* Welcome Page – auch für eingeloggte User zugänglich */}
          <Route path="/" element={<Welcome loggedIn={loggedIn} username={username} />} />
          
          {/* Login/Register auch für nicht eingeloggte */}
          <Route path="/login" element={<Login setLoggedIn={setLoggedIn} setUsername={setUsername} setUserId={setUserId} setRoleLevel={setRoleLevel} />} />
          <Route path="/register" element={<Register setLoggedIn={setLoggedIn} setUsername={setUsername} setUserId={setUserId} />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />

          {/* Info Pages - mit WelcomeHeader für alle (inkl. Login-Status) */}
          <Route path="/about" element={<About loggedIn={loggedIn} username={username} />} />
          <Route path="/docs" element={<Docs loggedIn={loggedIn} username={username} />} />
          <Route path="/contact" element={<Contact loggedIn={loggedIn} username={username} />} />
          <Route path="/impressum" element={<Impressum loggedIn={loggedIn} username={username} />} />
          <Route path="/datenschutz" element={<Privacy loggedIn={loggedIn} username={username} />} />
          <Route path="/privacy" element={<Privacy loggedIn={loggedIn} username={username} />} />
          <Route path="/agb" element={<Terms loggedIn={loggedIn} username={username} />} />
          <Route path="/terms" element={<Terms loggedIn={loggedIn} username={username} />} />
          
          {/* Home leitet zu Mein Regal weiter */}
          <Route
            path="/home"
            element={
              <ProtectedRoute loggedIn={loggedIn} sessionChecked={sessionChecked}>
                <Navigate to="/mein-regal" replace />
              </ProtectedRoute>
            }
          />
            
            {/* Protected Routes */}
            <Route
              path="/mein-regal"
              element={
                <ProtectedRoute loggedIn={loggedIn} sessionChecked={sessionChecked}>
                  <MeinRegal userId={userId} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/discover"
              element={
                <ProtectedRoute loggedIn={loggedIn} sessionChecked={sessionChecked}>
                  <Discover />
                </ProtectedRoute>
              }
            />
            <Route
              path="/friends"
              element={
                <ProtectedRoute loggedIn={loggedIn} sessionChecked={sessionChecked}>
                  <Friends />
                </ProtectedRoute>
              }
            />
            <Route
              path="/wishlist"
              element={
                <ProtectedRoute loggedIn={loggedIn} sessionChecked={sessionChecked}>
                  <Wishlist userId={userId} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute loggedIn={loggedIn} sessionChecked={sessionChecked}>
                  <Profile username={username} userId={userId} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile/:username"
              element={
                <ProtectedRoute loggedIn={loggedIn} sessionChecked={sessionChecked}>
                  <FriendProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute loggedIn={loggedIn} sessionChecked={sessionChecked}>
                  <Settings userId={userId} setLoggedIn={setLoggedIn} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/add-volume"
              element={
                <ProtectedRoute loggedIn={loggedIn} sessionChecked={sessionChecked}>
                  <AddVolume userId={userId} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute loggedIn={loggedIn} sessionChecked={sessionChecked}>
                  <Admin />
                </ProtectedRoute>
              }
            />
          </Routes>
        </main>

        <Footer />
        <CookieConsent />
      </div>
  );
}

function App() {
  const [loggedIn, setLoggedIn] = useState(false);
  const [username, setUsername] = useState('');
  const [userId, setUserId] = useState(null);
  const [roleLevel, setRoleLevel] = useState(4); // Default: USER (4)
  const [sessionChecked, setSessionChecked] = useState(false);

  // Gespeichertes Theme beim App-Start anwenden - zentral hier statt in
  // einzelnen Header-Komponenten, da z.B. Header.js (App-Header) das bisher
  // nie tat und ein harter Reload auf einer App-Seite (z.B. /wishlist) den
  // Light-Mode dadurch stillschweigend auf Dark zurückfallen ließ.
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'dark';
    document.documentElement.setAttribute('data-theme', savedTheme);
  }, []);

  // Session aus localStorage laden und mit Backend verifizieren
  useEffect(() => {
    const verifySession = async () => {
      const cookieUserId = getCookie('userId', true);
      const savedSession = localStorage.getItem('userSession');

      // Wenn kein lokaler Session-Hinweis vorhanden → direkt als geprüft markieren
      if (!savedSession && !cookieUserId) {
        setSessionChecked(true);
        return;
      }

      // Erst lokale Daten laden (für schnellen UI-Aufbau)
      if (savedSession) {
        try {
          const session = JSON.parse(savedSession);
          const finalUserId = cookieUserId ? parseInt(cookieUserId) : (session.userId || null);

          // Backend-Session verifizieren
          const result = await authApi.getMe();

          if (result.success && result.data) {
            // Session gültig → State setzen
            setLoggedIn(session.loggedIn || false);
            setUsername(result.data.username || session.username || '');
            setRoleLevel(result.data.role ?? session.roleLevel ?? 4);
            setUserId(result.data.id || finalUserId);
            console.log('✅ Session verifiziert:', result.data.username);

            // Sync-Grundlage: Änderungen seit letztem Besuch abrufen (Basis für
            // spätere Offline-Synchronisation, siehe src/api/services/sync.js)
            syncApi.syncChanges().catch(() => {});
          } else {
            // Session abgelaufen → alles clearen
            console.warn('⚠️ Session abgelaufen, wird gecleart');
            localStorage.removeItem('userSession');
            deleteCookie('userId');
            setLoggedIn(false);
            setUsername('');
            setUserId(null);
            setRoleLevel(4);
          }
        } catch (error) {
          console.error('Fehler beim Laden der Session:', error);
          localStorage.removeItem('userSession');
        }
      } else if (cookieUserId) {
        // Nur Cookie vorhanden → Backend-Check
        const result = await authApi.getMe();
        if (result.success && result.data) {
          setUserId(result.data.id || parseInt(cookieUserId));
        } else {
          deleteCookie('userId');
        }
      }

      setSessionChecked(true);
    };

    verifySession();
  }, []);

  // Session in localStorage speichern bei Änderungen
  useEffect(() => {
    if (loggedIn) {
      localStorage.setItem('userSession', JSON.stringify({
        loggedIn,
        username,
        userId,
        roleLevel
      }));
    } else {
      localStorage.removeItem('userSession');
    }
  }, [loggedIn, username, userId, roleLevel]);

  return (
    <ConfirmProvider>
      <Router>
        <AppContent
          loggedIn={loggedIn}
          setLoggedIn={setLoggedIn}
          username={username}
          setUsername={setUsername}
          userId={userId}
          setUserId={setUserId}
          roleLevel={roleLevel}
          setRoleLevel={setRoleLevel}
          sessionChecked={sessionChecked}
        />
      </Router>
    </ConfirmProvider>
  );
}

export default App;
